import React, {ReactNode, useCallback, useContext, useRef} from 'react';

import {CheckCircle} from '../../inputs/check-circle';
import {Button, ButtonAppearance} from '../../inputs/button';
import {Keys, cls} from '../../../utils';
import {RouteLinkProps} from '../../../../lib/routes';
import {LIST_ITEM_ATTR, ListItemIndexContext, ListRowRefContext, ListValue, useListContext} from '../list-context';

import css from './list-item.module.css';

type CheckmarkProps = Omit<React.ComponentProps<typeof CheckCircle>, 'selected' | 'onSelect'> & {
	onSelect?: (selected: boolean) => void;
};

type ItemElementProps = Omit<React.HTMLAttributes<HTMLLIElement | HTMLDivElement>, 'onClick' | 'title' | 'children'>;

export type ListItemProps = RouteLinkProps &
	ItemElementProps & {
		/** ключ пункта: по нему список считает пункт выделенным */
		value?: ListValue;
		children?: ReactNode;
		/** вид кнопки пункта; по умолчанию приглушённая */
		appearance?: ButtonAppearance;
		icon?: ReactNode;
		/** пропсы галочки, когда список показывает выделение галочками */
		checkmark?: CheckmarkProps;
		/**
		 * Действие над пунктом: клик, Enter или Space.
		 * preventDefault() в обработчике отменяет выделение пункта.
		 */
		onAction?(value: ListValue | undefined, index: number, event: React.SyntheticEvent): void;
		/** только клик мышью; preventDefault() так же отменяет выделение */
		onClick?(value: ListValue | undefined, index: number, event: React.MouseEvent): void;
		disabled?: boolean;
		/** подсказка кнопки */
		title?: string;
		/** класс самой кнопки */
		buttonClassName?: string;
		/** класс кнопки выбранного пункта */
		selectedClassName?: string;
		as?: 'li' | 'div';
		selected?: boolean;
		/** подпись переносится на новые строки; по умолчанию переносится */
		wrap?: boolean;
	};

export const ListItem: React.FC<ListItemProps> = ({
	value,
	children,
	appearance = 'subtle',
	icon,
	checkmark,
	onAction,
	onClick,
	route,
	routeParams,
	href,
	target,
	download,
	disabled,
	title,
	className,
	buttonClassName,
	selected,
	selectedClassName,
	role,
	as,
	wrap = true,
	...itemProps
}) => {
	const list = useListContext();
	const wrapperRef = useRef<HTMLLIElement & HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

	const isSelected = selected !== undefined ? selected : !!list?.isSelected(value);
	const withCheckmark = !!list && list.selectAppearance === 'checkbox' && !!list.selectMode;

	const ownIndex = useContext(ListItemIndexContext);
	const index = useCallback(
		() => (ownIndex >= 0 ? ownIndex : (list?.indexOf(wrapperRef.current) ?? -1)),
		[ownIndex, list],
	);
	const isActive = !!list?.virtualFocus && ownIndex >= 0 && list.activeIndex === ownIndex;
	const rowRef = useContext(ListRowRefContext);
	const handleWrapperRef = useCallback(
		(element: HTMLLIElement & HTMLDivElement) => {
			wrapperRef.current = element;
			rowRef?.(element);
		},
		[rowRef],
	);

	const handlePointerMove = useCallback(() => {
		if (list?.virtualFocus && ownIndex >= 0 && list.activeIndex !== ownIndex) list.setActiveIndex(ownIndex, false);
	}, [list, ownIndex]);

	const handleMouseDown = useCallback(
		(event: React.MouseEvent) => {
			if (list?.virtualFocus) event.preventDefault();
		},
		[list],
	);

	const action = useCallback(
		(event: React.SyntheticEvent) => {
			const position = index();
			if (onAction) onAction(value, position, event);
			if (!event.defaultPrevented) list?.toggle(value, event);
			list?.itemAction(value, position, event);
		},
		[index, onAction, value, list],
	);

	const handleClick = useCallback(
		(event: React.MouseEvent) => {
			if (onClick) onClick(value, index(), event);
			action(event);
		},
		[onClick, value, index, action],
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.keyCode !== Keys.ENTER && event.keyCode !== Keys.SPACE) return;
			// сначала действие: по отменённому событию пункт считает, что выбор отменили, и ничего не делает
			action(event);
			const isLink = !!(route || href);
			// гасим, чтобы не было ни прокрутки от пробела, ни второго срабатывания от клика
			if (!isLink || event.keyCode === Keys.SPACE) event.preventDefault();
		},
		[action, route, href],
	);

	const handleCheckmark = useCallback(
		(next: boolean) => {
			checkmark?.onSelect?.(next);
		},
		[checkmark],
	);

	const Tag = as || ((list?.itemTag || 'div') as 'li');

	return (
		<Tag
			{...itemProps}
			{...{[LIST_ITEM_ATTR]: ''}}
			ref={handleWrapperRef}
			id={list?.virtualFocus && ownIndex >= 0 ? list.idFor(ownIndex) : itemProps.id}
			data-index={ownIndex >= 0 ? ownIndex : undefined}
			onPointerMove={handlePointerMove}
			onMouseDown={handleMouseDown}
			className={cls(
				css.itemWrapper,
				list?.horizontal ? css.horizontal : css.vertical,
				isSelected && css.selected,
				list?.selectAppearance === 'line' && css.lineSelect,
				isActive && list?.activeFocusVisible && css.activeFocus,
				className,
			)}
			role={role ?? list?.itemRole}
			aria-selected={itemProps['aria-selected'] ?? (list?.selectMode ? isSelected : undefined)}
			aria-posinset={itemProps['aria-posinset'] ?? (list && ownIndex >= 0 ? ownIndex + 1 : undefined)}
			aria-setsize={itemProps['aria-setsize'] ?? (list ? list.size() || undefined : undefined)}
		>
			{withCheckmark && (
				<CheckCircle
					{...checkmark}
					selected={isSelected}
					onSelect={handleCheckmark}
					className={cls(css.checkCircle, checkmark?.className)}
				/>
			)}
			<Button
				route={route}
				routeParams={routeParams}
				href={href}
				target={target}
				download={download}
				disabled={disabled}
				title={title}
				appearance={appearance}
				icon={icon}
				wrap={wrap}
				checked={isSelected && (list?.selectAppearance ?? 'selected') === 'selected'}
				aria-current={isSelected && !list?.selectMode && (route || href) ? 'page' : undefined}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				className={cls(css.itemButton, buttonClassName, isSelected && selectedClassName)}
				setRef={buttonRef as React.RefObject<HTMLButtonElement | HTMLAnchorElement | null>}
				tabIndex={list?.navigationMode || list?.virtualFocus ? -1 : undefined}
			>
				{children}
			</Button>
		</Tag>
	);
};
